/**
 * The order the styles appear in the menu, which doubles as the precedence for reporting which
 * style a selection already matches. Text can match several styles at once, and the earlier entry
 * wins: a lone capitalized word reads as sentence case rather than as each-word capitalization.
 */
export const CAPITALIZATION_STYLES = [
  'uppercase',
  'lowercase',
  'sentence',
  'capitalize-each-word',
] as const;

export type CapitalizationStyle = (typeof CAPITALIZATION_STYLES)[number];

export const UNCHANGED_CAPITALIZATION_MESSAGE =
  "Can't change the case of one line. It contains a tab or another item that is not text, and the new text would be a different length.";

export const CAPITALIZATION_LABELS: Record<CapitalizationStyle, string> = {
  'capitalize-each-word': 'Capitalize Each Word',
  lowercase: 'Lowercase',
  sentence: 'Sentence Case',
  uppercase: 'Uppercase',
};

const FIRST_LETTER_TO_CAPITALIZE = /(?!\p{Lm})\p{L}/u;

const applyByStyle: Record<CapitalizationStyle, (text: string) => string> = {
  'capitalize-each-word': capitalizeEachWord,
  lowercase: text => text.toLowerCase(),
  sentence: toSentenceCase,
  uppercase: text => text.toUpperCase(),
};

export function applyCapitalization(text: string, style: CapitalizationStyle): string {
  if (!Object.hasOwn(applyByStyle, style)) return text;
  return applyByStyle[style](text);
}

export function capitalizationParts(text: string, style: CapitalizationStyle): string[] | null {
  switch (style) {
    case 'uppercase':
      return mappedParts(text, value => value.toUpperCase());
    case 'lowercase':
      return mappedParts(text, value => value.toLowerCase());
    case 'sentence':
      return sentenceParts(text);
    case 'capitalize-each-word':
      return wordParts(text);
  }
}

function toSentenceCase(text: string): string {
  // Breaks follow the uppercased text, so a period starts a new sentence whatever the next
  // letter's case is. Recasing uses the original letters, so ß is never turned into SS and back.
  const slices = originalSentenceSlices(text) ?? segmentSentences(text);
  let result = '';
  for (const slice of slices) result += capitalizeFirstLetter(slice);
  return result;
}

function segmentSentences(text: string): string[] {
  const segmenter = new Intl.Segmenter(undefined, { granularity: 'sentence' });
  return [...segmenter.segment(text)].map(({ segment }) => segment);
}

function originalSentenceSlices(text: string): string[] | null {
  const points = [...text];
  const upperParts = points.map(point => point.toUpperCase());
  const upper = upperParts.join('');
  if (upper !== text.toUpperCase()) return null;

  const slices: string[] = [];
  let pointIndex = 0;
  for (const segment of segmentSentences(upper)) {
    let remaining = [...segment].length;
    const start = pointIndex;
    while (pointIndex < points.length && remaining > 0) {
      const size = [...upperParts[pointIndex]].length;
      if (size > remaining) return null;
      remaining -= size;
      pointIndex += 1;
    }
    if (remaining !== 0) return null;
    slices.push(points.slice(start, pointIndex).join(''));
  }
  if (pointIndex !== points.length) return null;
  return slices;
}

function capitalizeEachWord(text: string): string {
  const segmenter = new Intl.Segmenter(undefined, { granularity: 'word' });
  let result = '';
  for (const { isWordLike, segment } of segmenter.segment(text)) {
    if (!isWordLike) {
      result += segment;
      continue;
    }
    result += capitalizeFirstLetter(segment);
  }
  return result;
}

function capitalizeFirstLetter(text: string): string {
  const lower = text.toLowerCase();
  const match = FIRST_LETTER_TO_CAPITALIZE.exec(lower);
  if (!match) return lower;
  const [letter] = match;
  const index = match.index;
  return lower.slice(0, index) + titleCaseLetter(letter) + lower.slice(index + letter.length);
}

function titleCaseLetter(letter: string): string {
  const points = [...letter.toUpperCase()];
  const capitalIndex = points.findIndex(point => /[\p{Lu}\p{Lt}]/u.test(point));
  if (capitalIndex < 0) return points.join('');
  return (
    points.slice(0, capitalIndex + 1).join('') +
    points
      .slice(capitalIndex + 1)
      .join('')
      .toLowerCase()
  );
}

function mappedParts(text: string, mapText: (value: string) => string): string[] | null {
  const parts = [...text].map(point => mapText(point));
  return reconcileCaseParts(parts, mapText(text));
}

function reconcileCaseParts(parts: string[], target: string): string[] | null {
  const source = parts.join('');
  if (source === target) return parts;

  const targetPoints = [...target];
  if ([...source].length !== targetPoints.length) return null;

  const result: string[] = [];
  let index = 0;
  for (const part of parts) {
    const count = [...part].length;
    result.push(targetPoints.slice(index, index + count).join(''));
    index += count;
  }
  if (result.join('') !== target) return null;
  return result;
}

function sentenceParts(text: string): string[] | null {
  const slices = originalSentenceSlices(text);
  if (!slices) return null;

  const result: string[] = [];
  for (const slice of slices) {
    const parts = capitalizeFirstLetterParts(slice);
    if (!parts) return null;
    result.push(...parts);
  }
  if (result.join('') !== toSentenceCase(text)) return null;
  return result;
}

function wordParts(text: string): string[] | null {
  const segmenter = new Intl.Segmenter(undefined, { granularity: 'word' });
  const result: string[] = [];
  for (const { isWordLike, segment } of segmenter.segment(text)) {
    if (isWordLike) {
      const parts = capitalizeFirstLetterParts(segment);
      if (!parts) return null;
      result.push(...parts);
    } else {
      result.push(...segment);
    }
  }
  if (result.join('') !== capitalizeEachWord(text)) return null;
  return result;
}

function capitalizeFirstLetterParts(text: string): string[] | null {
  const lowerParts = mappedParts(text, value => value.toLowerCase());
  if (!lowerParts) return null;

  const match = FIRST_LETTER_TO_CAPITALIZE.exec(lowerParts.join(''));
  if (!match) return lowerParts;

  const [letter] = match;
  let consumed = 0;
  const result = lowerParts.slice();
  for (const [index, part] of lowerParts.entries()) {
    if (consumed + part.length > match.index) {
      const indexInPart = match.index - consumed;
      result[index] =
        part.slice(0, indexInPart) +
        titleCaseLetter(letter) +
        part.slice(indexInPart + letter.length);
      break;
    }
    consumed += part.length;
  }
  if (result.join('') !== capitalizeFirstLetter(text)) return null;
  return result;
}
